// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LocationResultCard from "@stoneage/App/Components/Locations/LocationResultCard";
import { GridContainerProps } from "@stoneage/MobiusOverrides/GridContainer/GridContainer";
import { XLocationModel } from "@stoneage/Overrides/Components/LocationSearchForm";
import { fontConfig } from "@stoneage/XDL/constants";
import { color, styledScrollbar, xScreen } from "@stoneage/XDL/Utilities";
import React from "react";
import { css } from "styled-components";
import Filter from "@stoneage/XDL/Icons/Filter";

interface LocationResultsListProps {
    locations: XLocationModel[];
    distanceUnitOfMeasure: DistanceUnitOfMeasure;
    country: string;
    state: string;
    category: string;
    onOpenFilterModal: () => void;
    onLocationClick: (event: React.MouseEvent<Element, MouseEvent>, location: XLocationModel) => void;
}

interface LocationResultsListStyles {
    leftContainerStyledWrapper?: InjectableCss;
    searchResultRow?: InjectableCss;
    loadingGridContainer?: GridContainerProps;
    locationFilterAndCountWrapper?: InjectableCss;
    locationCount?: TypographyPresentationProps;
    locationFilterLink?: LinkPresentationProps;
}

const locationResultsListStyles: LocationResultsListStyles = {
    leftContainerStyledWrapper: {
        css: css`
            overflow: auto;
            display: flex;
            width: 100%;
            flex-grow: 1;
            padding: 12px 4px 0 0;
            background-color: ${color("common.ui4")};
            ${xScreen("lte", "md")} {
                flex-direction: column;
            }
        `,
    },
    searchResultRow: {
        css: css`
            overflow-y: auto;
            overflow-x: hidden;
            padding: 0 5px 12px 12px;
            width: 100%;
            ${styledScrollbar(4)}
            &::-webkit-scrollbar-thumb {
                margin-block: 4px;
            }
            ${xScreen("lte", "md")} {
                width: 100%;
                max-width: 100%;
                height: 100%;
            }
        `,
    },

    locationFilterAndCountWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            gap: 12px;
            padding-bottom: 12px;
        `,
    },
    locationCount: {
        ...fontConfig.smallSingleLine,
        weight: fontConfig.weight.regular,
    },
    locationFilterLink: {
        typographyProps: {
            css: css`
                white-space: nowrap;
            `,
        },
        icon: {
            position: "left",
            iconProps: {
                src: Filter,
                size: 15,
            },
        },
    },
};

const getFilterLabel = (country: string, state: string, category: string) =>
    translate(country || state || category ? "Edit Filters" : "Filter Results");

const styles = locationResultsListStyles;
const LocationResultsList: React.FC<LocationResultsListProps> = ({
    locations,
    distanceUnitOfMeasure,
    country,
    state,
    category,
    onOpenFilterModal,
    onLocationClick,
}) => (
    <StyledWrapper {...styles.leftContainerStyledWrapper}>
        <StyledWrapper {...styles.searchResultRow}>
            <StyledWrapper {...styles.locationFilterAndCountWrapper}>
                <Link {...styles.locationFilterLink} onClick={onOpenFilterModal}>
                    {getFilterLabel(country, state, category)}
                </Link>
                <Typography {...styles.locationCount}>
                    {translate("Showing {0} Locations", `${locations.length}`)}
                </Typography>
            </StyledWrapper>
            {locations.map(location => (
                <LocationResultCard
                    key={location.id}
                    location={location}
                    distanceUnitOfMeasure={distanceUnitOfMeasure}
                    onLocationClick={onLocationClick}
                />
            ))}
        </StyledWrapper>
    </StyledWrapper>
);

export default LocationResultsList;
